<div class="mt-6 w-full flex flex-col gap-5">
  <p-toast></p-toast>

  <p-card>
    <ng-template #title>
      <div class="flex items-center gap-3">
        <p-button
          icon="fa fa-arrow-left"
          [text]="true"
          [rounded]="true"
          severity="secondary"
          (click)="navigateBack()"
        />
        <h1 class="grow">
          @if (mode() === 'create') {
            {{ 'task.add' | translate }}
          } @else if (mode() === 'edit') {
            {{ 'task.editTitle' | translate }}
          } @else {
            {{ task()?.title }}
          }
        </h1>
        @if (mode() === 'view') {
          <p-button
            [label]="'label.edit' | translate"
            icon="fa fa-pencil"
            severity="secondary"
            [outlined]="true"
            (click)="startEditing()"
          />
        }
      </div>
    </ng-template>

    <!-- Current execution: status and actions -->
    @if (currentExecution(); as execution) {
      <div
        class="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 rounded-lg border border-surface-200 bg-surface-50"
      >
        <div class="flex flex-wrap items-center gap-3">
          <p-tag
            [value]="'task.statuses.' + execution.status | translate"
            [severity]="statusSeverity(execution.status)"
          />
          @if (execution.due_at) {
            <span
              [class.text-red-600]="execution.is_overdue"
              [class.font-semibold]="execution.is_overdue"
            >
              {{ 'task.dueAt' | translate }}:
              {{
                execution.due_at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin'
              }}
            </span>
          }
          @if (execution.missed_count > 0) {
            <p-badge
              [value]="
                'task.missedCount'
                  | translate: { count: execution.missed_count }
              "
              severity="danger"
            />
          }
          @if (task()?.is_recurring) {
            <span class="text-surface-500">
              <i class="fa-regular fa-repeat mr-1"></i>
              {{
                recurrenceLabelKey(
                  task()!.recurrence_unit!,
                  task()!.recurrence_every
                ) | translate: { count: task()!.recurrence_every }
              }}
            </span>
          }
          @if (execution.status === 'done' && task()?.requires_approval) {
            <span class="text-surface-500">
              <i class="fa-regular fa-hourglass-half mr-1"></i>
              {{ 'task.awaitingApproval' | translate }}
            </span>
          }
        </div>
        <div class="flex flex-wrap gap-2">
          @if (execution.status === 'open') {
            <p-button
              [label]="'task.markInProgress' | translate"
              icon="fa-regular fa-play"
              severity="secondary"
              [outlined]="true"
              (click)="setStatus('in_progress')"
            />
          }
          @if (
            execution.status === 'open' || execution.status === 'in_progress'
          ) {
            <p-button
              [label]="'task.markDone' | translate"
              icon="fa fa-check"
              severity="success"
              (click)="setStatus('done')"
            />
          }
          @if (
            execution.status === 'done' &&
            task()?.requires_approval &&
            canApprove()
          ) {
            <p-button
              [label]="'task.approve' | translate"
              icon="fa-regular fa-badge-check"
              severity="success"
              (click)="setStatus('accepted')"
            />
          }
          @if (execution.status === 'done' || execution.status === 'accepted') {
            <p-button
              [label]="'task.reopen' | translate"
              icon="fa-regular fa-rotate-left"
              severity="secondary"
              [outlined]="true"
              (click)="setStatus('in_progress')"
            />
          }
        </div>
      </div>
    }

    <!-- Read-only view of the task definition -->
    @if (!showForm() && task(); as t) {
      <div class="flex flex-col md:flex-row gap-6">
        <div class="w-full md:w-2/3 flex flex-col gap-4">
          @if (t.description) {
            <div class="flex flex-col gap-1">
              <span class="text-sm text-surface-500">{{
                'task.description' | translate
              }}</span>
              <p class="m-0 whitespace-pre-line">{{ t.description }}</p>
            </div>
          }
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.recurrence.label' | translate
            }}</span>
            <span>
              @if (t.is_recurring) {
                <i class="fa-regular fa-repeat mr-1"></i>
                {{
                  recurrenceLabelKey(t.recurrence_unit!, t.recurrence_every)
                    | translate: { count: t.recurrence_every }
                }}
              } @else {
                {{ 'task.recurrence.none' | translate }}
              }
            </span>
          </div>
        </div>
        <div class="w-full md:w-1/3 flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.responsibleUser' | translate
            }}</span>
            <span>{{
              t.responsible_user?.name ?? ('task.notAssigned' | translate)
            }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.priority' | translate
            }}</span>
            <span>
              @if (t.priority) {
                {{ priorityLabelKey(t.priority) | translate }}
              } @else {
                –
              }
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.estimatedTime' | translate
            }}</span>
            <span>{{
              t.estimated_time !== null
                ? (t.estimated_time | number: '1.0-2') + ' h'
                : '–'
            }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.requiresApproval' | translate
            }}</span>
            <span>{{
              (t.requires_approval ? 'task.yes' : 'task.no') | translate
            }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-500">{{
              'task.createdBy' | translate
            }}</span>
            <span>
              {{ t.creator?.name ?? '?' }},
              {{ t.created_at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin' }}
            </span>
          </div>
        </div>
      </div>
    }

    <!-- Existing Images -->
    @if (task()?.media?.length) {
      <div class="flex flex-col gap-2 mt-6">
        <span class="font-medium">{{ 'task.images' | translate }}</span>
        <div class="image-gallery flex flex-wrap gap-3">
          @for (media of task()!.media; track media.id) {
            <div class="image-container">
              <button
                type="button"
                class="delete-icon fa fa-times-circle text-red-500 bg-transparent border-0 p-0"
                [attr.aria-label]="'label.delete' | translate"
                (click)="$event.stopPropagation(); deleteMedia(media)"
              ></button>
              <p-image
                [src]="getMediaUrl(media.url)"
                [alt]="media.name"
                [preview]="true"
                width="128"
                height="128"
                [imageStyle]="{
                  width: '128px',
                  height: '128px',
                  'object-fit': 'cover',
                  'border-radius': '0.5rem',
                }"
              >
                <ng-template #indicator>
                  <i class="fa fa-search"></i>
                </ng-template>
              </p-image>
            </div>
          }
        </div>
      </div>
    }

    <!-- Existing task: images are added while working on it and upload right away -->
    @if (mode() !== 'create') {
      <div class="flex flex-col gap-2 mt-6">
        <span class="font-medium">{{ 'task.uploadImages' | translate }}</span>
        <p-fileupload
          #immediateUpload
          name="files[]"
          [multiple]="true"
          accept="image/*"
          [maxFileSize]="10485760"
          [auto]="true"
          [customUpload]="true"
          (uploadHandler)="uploadImages($event.files, immediateUpload)"
          [chooseLabel]="'label.fileChoose' | translate"
          [cancelLabel]="'label.cancel' | translate"
        >
          <ng-template #empty>
            <div>{{ 'label.fileDragDrop' | translate }}</div>
          </ng-template>
        </p-fileupload>
      </div>
    }

    @if (showForm()) {
      <form
        [formGroup]="form"
        (ngSubmit)="onSubmit()"
        class="flex flex-col gap-6 mt-6"
      >
        <div class="flex flex-col md:flex-row gap-6">
          <div class="w-full md:w-2/3 flex flex-col gap-4">
            <!-- Title -->
            <div class="flex flex-col gap-2">
              <label for="title">{{ 'task.title' | translate }} *</label>
              <input
                pInputText
                id="title"
                formControlName="title"
                [placeholder]="'task.title' | translate"
                class="w-full"
                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('title') }"
              />
              @if (isInvalid('title')) {
                <small class="text-red-500">{{
                  getErrorKey('title') | translate
                }}</small>
              }
            </div>

            <!-- Description -->
            <div class="flex flex-col gap-2">
              <label for="description">{{
                'task.description' | translate
              }}</label>
              <textarea
                pTextarea
                id="description"
                formControlName="description"
                [placeholder]="'task.description' | translate"
                rows="5"
                class="w-full"
                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('description') }"
              ></textarea>
              @if (isInvalid('description')) {
                <small class="text-red-500">{{
                  getErrorKey('description') | translate
                }}</small>
              }
            </div>

            <!-- Recurrence -->
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-3">
                <p-toggleswitch
                  inputId="isRecurring"
                  formControlName="isRecurring"
                />
                <label for="isRecurring">{{
                  'task.recurrence.enable' | translate
                }}</label>
              </div>
              @if (form.value.isRecurring) {
                <div class="flex items-center gap-2">
                  <span>{{ 'task.recurrence.every.prefix' | translate }}</span>
                  <p-inputNumber
                    inputId="recurrenceEvery"
                    formControlName="recurrenceEvery"
                    [min]="1"
                    [max]="1000"
                    [showButtons]="true"
                    [useGrouping]="false"
                    inputStyleClass="w-20"
                    [ngClass]="{
                      'ng-invalid ng-dirty': isInvalid('recurrenceEvery'),
                    }"
                  />
                  <p-select
                    inputId="recurrenceUnit"
                    [options]="recurrenceUnitOptions"
                    optionLabel="labelKey"
                    optionValue="key"
                    formControlName="recurrenceUnit"
                  >
                    <ng-template #selectedItem let-option>
                      {{ option.labelKey | translate }}
                    </ng-template>
                    <ng-template #item let-option>
                      {{ option.labelKey | translate }}
                    </ng-template>
                  </p-select>
                </div>
                <small class="text-surface-500">{{
                  'task.recurrence.hint' | translate
                }}</small>
                @if (isInvalid('recurrenceEvery')) {
                  <small class="text-red-500">{{
                    getErrorKey('recurrenceEvery') | translate
                  }}</small>
                }
              }
            </div>
          </div>

          <div class="w-full md:w-1/3 flex flex-col gap-4">
            <!-- Due At -->
            <div class="flex flex-col gap-2">
              <label for="dueAt">{{ 'task.dueAt' | translate }}</label>
              <p-datepicker
                inputId="dueAt"
                formControlName="dueAt"
                dateFormat="dd.mm.yy"
                [showTime]="true"
                hourFormat="24"
                [showClear]="true"
                [disabled]="
                  mode() === 'edit' &&
                  !(
                    currentExecution()?.status === 'open' ||
                    currentExecution()?.status === 'in_progress'
                  )
                "
                class="w-full"
                appendTo="body"
              />
            </div>

            <!-- Responsible User -->
            <div class="flex flex-col gap-2">
              <label for="responsibleUser">{{
                'task.responsibleUser' | translate
              }}</label>
              <p-select
                inputId="responsibleUser"
                [options]="responsibleUserOptions()"
                optionLabel="label"
                optionValue="value"
                formControlName="responsibleUserId"
                [placeholder]="'task.notAssigned' | translate"
                class="w-full"
                [showClear]="true"
                [filter]="true"
                appendTo="body"
              />
            </div>

            <!-- Priority -->
            <div class="flex flex-col gap-2">
              <label for="priority">{{ 'task.priority' | translate }}</label>
              <p-select
                inputId="priority"
                [options]="priorityOptions"
                optionLabel="labelKey"
                optionValue="key"
                formControlName="priority"
                [placeholder]="'task.priority' | translate"
                class="w-full"
                [showClear]="true"
              >
                <ng-template #selectedItem let-option>
                  {{ option.labelKey | translate }}
                </ng-template>
                <ng-template #item let-option>
                  {{ option.labelKey | translate }}
                </ng-template>
              </p-select>
            </div>

            <!-- Estimated Time -->
            <div class="flex flex-col gap-2">
              <label for="estimatedTime">{{
                'task.estimatedTime' | translate
              }}</label>
              <p-inputNumber
                inputId="estimatedTime"
                formControlName="estimatedTime"
                [placeholder]="'task.estimatedTime' | translate"
                [useGrouping]="false"
                mode="decimal"
                [min]="0"
                [maxFractionDigits]="2"
                suffix=" h"
                styleClass="w-full"
              />
            </div>

            <!-- Requires Approval -->
            <div class="flex items-center gap-2">
              <p-checkbox
                inputId="requiresApproval"
                formControlName="requiresApproval"
                [binary]="true"
              />
              <label for="requiresApproval">{{
                'task.requiresApproval' | translate
              }}</label>
            </div>
          </div>
        </div>

        <!-- Upload Images (new task: sent together with the form) -->
        @if (mode() === 'create') {
          <div class="flex flex-col gap-2">
            <span class="font-medium">{{
              'task.uploadImages' | translate
            }}</span>
            <p-fileupload
              #fileUpload
              name="files[]"
              [multiple]="true"
              accept="image/*"
              [maxFileSize]="10485760"
              [auto]="false"
              [showUploadButton]="false"
              [chooseLabel]="'label.fileChoose' | translate"
              [cancelLabel]="'label.cancel' | translate"
            >
              <ng-template #empty>
                <div>{{ 'label.fileDragDrop' | translate }}</div>
              </ng-template>
            </p-fileupload>
          </div>
        }

        <!-- Comment field in create mode (inside form) -->
        @if (mode() === 'create') {
          <div class="flex flex-col gap-2">
            <label for="newComment">{{ 'task.addComment' | translate }}</label>
            <textarea
              pTextarea
              id="newComment"
              [ngModel]="newCommentText()"
              (ngModelChange)="newCommentText.set($event)"
              [ngModelOptions]="{ standalone: true }"
              [placeholder]="'task.commentPlaceholder' | translate"
              rows="3"
              class="w-full"
            ></textarea>
          </div>
        }

        <!-- Action Buttons -->
        <div class="flex justify-end gap-2">
          <p-button
            [label]="'label.cancel' | translate"
            severity="secondary"
            (click)="cancelForm()"
          />
          <p-button [label]="'label.save' | translate" type="submit" />
        </div>
      </form>
    }

    <!-- History: comments with execution events in between -->
    @if (mode() !== 'create') {
      <p-panel
        [header]="'task.timeline.title' | translate"
        styleClass="bg-surface-100 mt-8"
      >
        <div class="flex flex-col gap-2 mb-6">
          <label for="newComment">{{ 'task.addComment' | translate }}</label>
          <textarea
            pTextarea
            id="newComment"
            [ngModel]="newCommentText()"
            (ngModelChange)="newCommentText.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="'task.commentPlaceholder' | translate"
            rows="3"
            class="w-full"
          ></textarea>
          <div class="flex justify-end">
            <p-button
              [label]="'task.commentSend' | translate"
              (click)="submitComment()"
              [disabled]="!newCommentText().trim()"
            />
          </div>
        </div>

        @if (timeline().length) {
          <div class="flex flex-col gap-3">
            @for (entry of paginatedTimeline(); track $index) {
              @if (entry.kind === 'comment') {
                <div class="bg-white border border-surface-200 rounded-lg p-4">
                  <div class="flex justify-between items-center mb-2">
                    <span class="font-semibold">
                      {{ entry.comment.user?.name ?? 'Unknown' }}
                    </span>
                    <div class="flex items-center gap-2">
                      <span class="text-sm text-surface-500">
                        {{
                          entry.at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin'
                        }}
                      </span>
                      <p-button
                        icon="fa fa-trash"
                        [text]="true"
                        [rounded]="true"
                        severity="danger"
                        size="small"
                        (click)="removeComment(entry.comment)"
                      />
                    </div>
                  </div>
                  <p class="m-0 whitespace-pre-line">
                    {{ entry.comment.comment }}
                  </p>
                </div>
              } @else {
                <div
                  class="flex items-center gap-2 px-4 text-sm italic text-surface-500"
                >
                  <i [class]="entry.icon"></i>
                  <span>{{ entry.labelKey | translate: entry.params }}</span>
                  <span class="ml-auto not-italic">
                    {{ entry.at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin' }}
                  </span>
                </div>
              }
            }
          </div>

          @if (timeline().length > timelinePerPage) {
            <p-paginator
              styleClass="mt-4"
              [rows]="timelinePerPage"
              [totalRecords]="timeline().length"
              [first]="timelineFirst()"
              (onPageChange)="onTimelinePageChange($event)"
            />
          }
        }
      </p-panel>
    }
  </p-card>
</div>
